import {
  FormControl,
  FormHelperText,
  TextField as MuiTextField,
  Typography,
} from '@mui/material'
import type { TextFieldProps as MuiTextFieldProps } from '@mui/material/TextField'
import { alpha, useTheme } from '@mui/material/styles'
import type { Control } from 'react-hook-form'
import { Controller } from 'react-hook-form'

import LabelWithTooltip from '@/components/LabelWithTooltip'

export interface CustomTextFieldProps
  extends Omit<MuiTextFieldProps, 'value' | 'onChange'> {
  name: string
  control: Control<any>
  title?: string
  tooltipText?: string
  titleSize?: 'small' | 'medium' | 'large'
  error?: boolean
  required?: boolean
  fullWidth?: boolean
  width?: string
  align?: 'left' | 'center' | 'right'
  onFormValueChange?: (name: string, value: any) => void
  showError?: boolean
}

const TextField = ({
  name,
  control,
  title,
  tooltipText,
  titleSize = 'large',
  error = false,
  required = false,
  fullWidth = false,
  size = 'medium',
  disabled = false,
  width = '100%',
  align = 'left',
  sx,
  showError = true,
  onFormValueChange,
  ...rest
}: CustomTextFieldProps) => {
  const theme = useTheme()

  const getTitleFontSize = (size: 'small' | 'medium' | 'large') => {
    switch (size) {
      case 'small':
        return '0.875rem'
      case 'medium':
        return '1.25rem'
      case 'large':
        return '1.50rem'
      default:
        return '1rem'
    }
  }

  const renderTitle = () => {
    if (!title) return null

    if (titleSize === 'large') {
      return (
        <LabelWithTooltip
          label={title}
          tooltipText={tooltipText || title}
          titleSx={{
            fontSize: getTitleFontSize(titleSize),
            fontWeight: 500,
            lineHeight: 'normal',
          }}
        />
      )
    }

    return (
      <Typography
        fontSize={getTitleFontSize(titleSize)}
        fontWeight={500}
        lineHeight="normal"
        mb={0.5}
      >
        {title}
      </Typography>
    )
  }

  return (
    <FormControl
      error={error}
      required={required}
      fullWidth={fullWidth}
      size={size}
      disabled={disabled}
      sx={{ width }}
    >
      {renderTitle()}
      <Controller
        name={name}
        control={control}
        render={({
          field: { onChange, value, ...field },
          fieldState: { error: fieldError },
        }) => (
          <>
            <MuiTextField
              {...field}
              value={value || ''}
              sx={{
                backgroundColor: theme.palette.background.paper,
                '& .MuiOutlinedInput-root': {
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor:
                      fieldError && !value
                        ? theme.palette.error.main
                        : theme.palette.action.disabled,
                    borderWidth: '1px',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor:
                      fieldError && !value
                        ? theme.palette.error.main
                        : theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor:
                      fieldError && !value
                        ? theme.palette.error.main
                        : theme.palette.primary.main,
                    borderWidth: '1px',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: alpha(theme.palette.action.disabled, 0.12),
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.action.disabled,
                    },
                  },
                  '& .MuiInputBase-input': {
                    paddingRight: '12px',
                    paddingLeft: '12px',
                    letterSpacing: '0.15px',
                    textAlign: align,
                    '&[type=number]': {
                      '&::-webkit-outer-spin-button': {
                        opacity: 1,
                        height: '40%',
                        margin: 0,
                        cursor: 'pointer',
                        position: 'absolute',
                        right: '4px',
                      },
                      '&::-webkit-inner-spin-button': {
                        opacity: 1,
                        height: '40%',
                        margin: 0,
                        cursor: 'pointer',
                        position: 'absolute',
                        right: '4px',
                      },
                    },
                  },
                },
                ...sx,
              }}
              onChange={(event) => {
                onChange(event)
                if (onFormValueChange) {
                  onFormValueChange(name, event.target.value)
                }
              }}
              {...rest}
            />
            {showError && fieldError && (
              <FormHelperText sx={{ color: theme.palette.error.main }}>
                {fieldError.message}
              </FormHelperText>
            )}
          </>
        )}
      />
    </FormControl>
  )
}

export default TextField
